import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchCachedValue,
  readCachedValue,
  subscribeToCache,
} from './query-cache';
import type { PulseCacheKey } from './cache-keys';

type CachedQueryState<T> = {
  generation: number;
  loading: boolean;
  refreshing: boolean;
  value?: T;
  error?: Error;
};

type UseCachedQueryOptions<T> = {
  cacheKey: PulseCacheKey;
  fetcher: () => Promise<T>;
  staleTimeMs: number;
  cacheTimeMs: number;
  enabled?: boolean;
  useSharedCache?: boolean;
};

export const useCachedQuery = <T>({
  cacheKey,
  fetcher,
  staleTimeMs,
  cacheTimeMs,
  enabled = true,
  useSharedCache = true,
}: UseCachedQueryOptions<T>) => {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const activeQueryRef = useRef({
    cacheKey,
    enabled,
    useSharedCache,
    generation: 0,
  });
  const executionGenerationRef = useRef(0);
  if (
    activeQueryRef.current.cacheKey !== cacheKey ||
    activeQueryRef.current.enabled !== enabled ||
    activeQueryRef.current.useSharedCache !== useSharedCache
  ) {
    activeQueryRef.current = {
      cacheKey,
      enabled,
      useSharedCache,
      generation: activeQueryRef.current.generation + 1,
    };
  }
  const generation = activeQueryRef.current.generation;
  const cached =
    enabled && useSharedCache ? readCachedValue<T>(cacheKey) : undefined;
  const [state, setState] = useState<CachedQueryState<T>>({
    generation,
    loading: enabled && !cached,
    refreshing: false,
    value: cached?.value,
  });

  const execute = useCallback(
    async (force = false) => {
      const executionGeneration = executionGenerationRef.current + 1;
      executionGenerationRef.current = executionGeneration;
      const isActiveExecution = () =>
        activeQueryRef.current.generation === generation &&
        executionGenerationRef.current === executionGeneration;

      if (!enabled) {
        setState(previous =>
          isActiveExecution()
            ? { generation, loading: false, refreshing: false }
            : previous,
        );
        return undefined;
      }

      const current = useSharedCache ? readCachedValue<T>(cacheKey) : undefined;
      if (!force && current && !current.isStale) {
        setState(previous =>
          isActiveExecution()
            ? {
                generation,
                loading: false,
                refreshing: false,
                value: current.value,
              }
            : previous,
        );
        return current.value;
      }

      setState(previous =>
        isActiveExecution()
          ? {
              generation,
              loading: !current,
              refreshing: Boolean(current),
              value: current?.value,
            }
          : previous,
      );

      try {
        const value = useSharedCache
          ? await fetchCachedValue({
              cacheKey,
              fetcher: () => fetcherRef.current(),
              staleTimeMs,
              cacheTimeMs,
              force,
            })
          : await fetcherRef.current();
        setState(previous =>
          isActiveExecution()
            ? { generation, loading: false, refreshing: false, value }
            : previous,
        );
        return value;
      } catch (error) {
        setState(previous =>
          isActiveExecution()
            ? {
                generation,
                loading: false,
                refreshing: false,
                value: previous.value,
                error:
                  error instanceof Error ? error : new Error(String(error)),
              }
            : previous,
        );
        return undefined;
      }
    },
    [cacheKey, cacheTimeMs, enabled, generation, staleTimeMs, useSharedCache],
  );

  useEffect(() => {
    if (!useSharedCache) {
      void execute();
      return undefined;
    }

    const unsubscribe = subscribeToCache(cacheKey, () => {
      if (!enabled) {
        return;
      }

      const next = readCachedValue<T>(cacheKey);
      if (next) {
        setState(previous =>
          activeQueryRef.current.generation === generation
            ? {
                generation,
                loading: false,
                refreshing: false,
                value: next.value,
              }
            : previous,
        );
      } else {
        execute(true);
      }
    });
    execute();
    return unsubscribe;
  }, [cacheKey, enabled, execute, generation, useSharedCache]);

  const activeState: CachedQueryState<T> =
    state.generation === generation
      ? state
      : {
          generation,
          loading: enabled && !cached,
          refreshing: false,
          value: cached?.value,
        };

  return {
    loading: activeState.loading,
    refreshing: activeState.refreshing,
    value: activeState.value,
    error: activeState.error,
    retry: () => execute(true),
  };
};
